import React, { useEffect, useMemo, useState } from "react";
import "./AddOpportunityModal.css";
import { authFetch } from "../../utils/authFetch";
import CustomFieldsSection from "../custom-fields/CustomFieldsSection";

const PLAN_ID_COVERAGE_TYPES = ["MAPD", "PDP"];

const getPlanYearFromDate = (dateValue) => {
  if (!dateValue) return "";
  const year = new Date(`${dateValue}T00:00:00`).getFullYear();
  return Number.isNaN(year) ? "" : String(year);
};

const AddOpportunityModal = ({ onClose, onSubmit, leadId = null, clientId = null }) => {
  const [step, setStep] = useState(1);

  const [coverageTypes, setCoverageTypes] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [opportunityStatuses, setOpportunityStatuses] = useState([]);

  const [selectedCoverageType, setSelectedCoverageType] = useState("");
  const [selectedPlanTypeId, setSelectedPlanTypeId] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");

  const [coverageTypeLoading, setCoverageTypeLoading] = useState(false);

  const [formData, setFormData] = useState({
    status: "",
    carrierId: "",
    planName: "",
    planId: "",
    benefitAmount: "",
    termLength: "",
    benefitOption: "",
    enrollmentPeriod: "",
    applicationDate: "",
    effectiveDate: "",
    premium: "",
    nextStep: "",
    dueDate: "",
    policyNumber: "",
    enrollmentCode: "",
    faceValue: "",
  });

  const [customFieldValues, setCustomFieldValues] = useState([]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [coverageRes, statusesRes] = await Promise.all([
          authFetch("/api/coverage-types"),
          authFetch("/api/policy-statuses?type=opportunity"),
        ]);

        const coverageData = await coverageRes.json().catch(() => []);
        const statusesData = await statusesRes.json().catch(() => []);

        setCoverageTypes(Array.isArray(coverageData) ? coverageData : []);
        setOpportunityStatuses(Array.isArray(statusesData) ? statusesData : []);
      } catch (err) {
        console.error("Load opportunity modal lookup data error:", err);
        setCoverageTypes([]);
        setOpportunityStatuses([]);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const loadCoverageTypeData = async () => {
      if (!selectedCoverageType) {
        setPlanTypes([]);
        setCarriers([]);
        setFormData((prev) => ({
          ...prev,
          carrierId: "",
        }));
        return;
      }

      try {
        setCoverageTypeLoading(true);

        const [planTypesRes, carriersRes] = await Promise.all([
          authFetch(`/api/coverage-types/${selectedCoverageType}/plan-types`),
          authFetch(`/api/subscriber-carriers/coverage-type/${selectedCoverageType}`),
        ]);

        const planTypesData = await planTypesRes.json().catch(() => []);
        const carriersData = await carriersRes.json().catch(() => ({}));

        const nextPlanTypes = Array.isArray(planTypesData) ? planTypesData : [];

        const nextCarriers = Array.isArray(carriersData?.carriers)
          ? carriersData.carriers
          : Array.isArray(carriersData)
            ? carriersData
            : [];

        setPlanTypes(nextPlanTypes);
        setCarriers(nextCarriers);

        setFormData((prev) => {
          if (!prev.carrierId) return prev;

          const carrierStillAvailable = nextCarriers.some(
            (carrier) => String(carrier.id) === String(prev.carrierId)
          );

          if (carrierStillAvailable) return prev;

          return {
            ...prev,
            carrierId: "",
            benefitOption: "",
            benefitAmount: "",
          };
        });
      } catch (err) {
        console.error("Load opportunity coverage type data error:", err);
        setPlanTypes([]);
        setCarriers([]);
        setFormData((prev) => ({
          ...prev,
          carrierId: "",
        }));
      } finally {
        setCoverageTypeLoading(false);
      }
    };

    loadCoverageTypeData();
  }, [selectedCoverageType]);

  const selectedTypeObj = useMemo(
    () => coverageTypes.find((ct) => Number(ct.id) === Number(selectedCoverageType)),
    [coverageTypes, selectedCoverageType]
  );

  const selectedTypeName = selectedTypeObj ? selectedTypeObj.name : "";
  const shouldShowPlanId = PLAN_ID_COVERAGE_TYPES.includes(selectedTypeName);
  const shouldShowPlanName = selectedTypeName !== "Medicare Supplement";

  const selectedCarrier = useMemo(
    () => carriers.find((c) => String(c.id) === String(formData.carrierId)),
    [carriers, formData.carrierId]
  );

  const needsSubtypeStep = planTypes.length > 0;

  const currentStepTitle =
    step === 1
      ? "Select Coverage Type"
      : step === 2
      ? selectedTypeName === "Individual Health"
        ? "Choose Individual Health Type"
        : selectedTypeName === "Life"
        ? "Choose Life Insurance Type"
        : selectedTypeName === "Medicare Supplement"
        ? "Choose Medicare Supplement Plan Type"
        : "Choose Plan Type"
      : "Opportunity Details";

  const currentStepSubtitle =
    step === 1
      ? "Choose the product category to tailor the rest of the opportunity form."
      : step === 2
      ? "Select the plan type before entering quote and follow-up details."
      : "Complete the quote setup, plan details, dates, and next actions.";

  const handleCarrierChange = (e) => {
    const { value } = e.target;

    setFormData((prev) => ({
      ...prev,
      carrierId: value,
      benefitOption: "",
      benefitAmount: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNext = () => {
    if (!selectedCoverageType || coverageTypeLoading) return;
    setStep(needsSubtypeStep ? 2 : 3);
  };

  const handleNextFromSubtype = () => {
    if (!selectedPlanTypeId) return;
    setStep(3);
  };

  const renderCarrierSelect = () => (
    <div className="form-group">
      <label>Carrier</label>
      <select
        name="carrierId"
        value={formData.carrierId}
        onChange={handleCarrierChange}
        disabled={!selectedCoverageType || coverageTypeLoading || carriers.length === 0}
      >
        <option value="">
          {coverageTypeLoading
            ? "-- Loading carriers --"
            : carriers.length === 0
              ? "-- No carriers configured --"
              : "-- Select Carrier --"}
        </option>
        {carriers.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name || c.carrier_name}
          </option>
        ))}
      </select>

      {selectedCoverageType && !coverageTypeLoading && carriers.length === 0 && (
        <small>
          No carriers have been selected for this coverage type. Manage carriers in Settings.
        </small>
      )}
    </div>
  );

  const renderPlanNameInput = () => (
    <div className="form-group">
      <label>Plan Name</label>
      <input
        type="text"
        name="planName"
        value={formData.planName}
        onChange={handleChange}
        placeholder="Enter plan/product name"
      />
    </div>
  );

  const renderPlanIdInput = () =>
    shouldShowPlanId ? (
      <div className="form-group">
        <label>Plan ID</label>
        <input
          type="text"
          name="planId"
          value={formData.planId}
          onChange={handleChange}
          placeholder="Example: H1234-001"
        />
      </div>
    ) : null;

  const handleSubmit = async () => {
    const payload = {
      status: formData.status || null,
      carrier_id: parseInt(formData.carrierId, 10) || null,
      product_id: null,
      plan_name: shouldShowPlanName ? formData.planName || null : null,
      plan_id: shouldShowPlanId ? formData.planId || null : null,
      coverage_type_id: parseInt(selectedCoverageType, 10) || null,
      coverage_type_plan_type_id: selectedPlanTypeId
        ? parseInt(selectedPlanTypeId, 10)
        : null,
      application_date: formData.applicationDate || null,
      effective_date: formData.effectiveDate || null,
      premium: formData.premium || null,
      next_step: formData.nextStep || null,
      due_date: formData.dueDate || null,
      policy_number: formData.policyNumber || null,
      enrollment_code: formData.enrollmentCode || null,
      face_value: formData.faceValue || null,
      election_period: formData.enrollmentPeriod || null,
      benefit_amount: formData.benefitAmount || null,
      term_length: formData.termLength || null,
      health_plan_type: selectedTypeName === "Individual Health" ? selectedSubtype || null : null,
      benefit_option: formData.benefitOption || null,
      plan_type: selectedSubtype || null,
      plan_year: getPlanYearFromDate(formData.effectiveDate) || null,
      lead_id: leadId,
      client_id: clientId,
    };

    try {
      const createdOpportunity = await onSubmit(payload);

      const createdOpportunityId =
        createdOpportunity?.id ||
        createdOpportunity?.opportunity?.id ||
        createdOpportunity?.insertId;

      if (createdOpportunityId && customFieldValues.length > 0) {
        const customFieldRes = await authFetch(
          `/api/custom-fields/values/opportunity/${createdOpportunityId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              values: customFieldValues,
            }),
          }
        );

        const customFieldData = await customFieldRes.json().catch(() => ({}));

        if (!customFieldRes.ok) {
          throw new Error(customFieldData.error || "Opportunity created, but custom fields failed to save.");
        }
      }

      onClose();
    } catch (err) {
      console.error("Add opportunity error:", err);
      alert(err.message || "Failed to add opportunity.");
    }
  };

  const renderCoverageSpecificFields = () => {
    switch (selectedTypeName) {
      case "MAPD":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
            {renderPlanIdInput()}

            <div className="form-group">
              <label>Enrollment Period</label>
              <select
                name="enrollmentPeriod"
                value={formData.enrollmentPeriod}
                onChange={handleChange}
              >
                <option value="">-- Select Period --</option>
                <option value="IEP">IEP</option>
                <option value="ICEP">ICEP</option>
                <option value="OEP">OEP</option>
                <option value="AEP">AEP</option>
                <option value="SEP">SEP</option>
              </select>
            </div>
          </>
        );

      case "PDP":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
            {renderPlanIdInput()}
          </>
        );

      case "Dental":
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            <div className="form-group">
              <label>Benefit Amount</label>
              <input
                type="text"
                name="benefitAmount"
                value={formData.benefitAmount || ""}
                onChange={handleChange}
                placeholder="Optional"
              />
            </div>
          </>
        );

      case "Medicare Supplement":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <input value={selectedSubtype} readOnly />
            </div>

            {renderCarrierSelect()}

            <div className="form-group">
              <label>Enrollment Period</label>
              <select
                name="enrollmentPeriod"
                value={formData.enrollmentPeriod}
                onChange={handleChange}
              >
                <option value="">-- Select Period --</option>
                <option value="Open Enrollment">Open Enrollment</option>
                <option value="Underwritten">Underwritten</option>
                <option value="Guaranteed Issue">Guaranteed Issue</option>
              </select>
            </div>
          </>
        );

      case "Life":
        return (
          <>
            <div className="form-group">
              <label>Plan Type</label>
              <input value={selectedSubtype} readOnly />
            </div>

            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            <div className="form-group">
              <label>Face Value</label>
              <input
                type="number"
                name="faceValue"
                value={formData.faceValue}
                onChange={handleChange}
              />
            </div>
          </>
        );

      case "Individual Health":
        return (
          <>
            <div className="form-group">
              <label>Health Type</label>
              <input value={selectedSubtype} readOnly />
            </div>

            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}

            {selectedSubtype === "Short-Term Health" && (
              <>
                <div className="form-group">
                  <label>Benefit Amount</label>
                  <input
                    type="number"
                    name="benefitAmount"
                    value={formData.benefitAmount}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Term Length (months)</label>
                  <input
                    type="number"
                    name="termLength"
                    value={formData.termLength}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
          </>
        );

      default:
        return (
          <>
            {renderCarrierSelect()}
            {shouldShowPlanName && renderPlanNameInput()}
          </>
        );
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg add-opportunity-modal redesigned-add-opportunity-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-shell">
          <div className="modal-header add-opportunity-header">
            <div>
              <h3>{currentStepTitle}</h3>
              <p className="modal-subtitle">{currentStepSubtitle}</p>
            </div>

            <button type="button" className="modal-close-btn" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="modal-body add-opportunity-body">
            {step === 1 && (
              <div className="add-opportunity-panel">
                <div className="add-opportunity-panel-head">
                  <h4>Select Coverage Type</h4>
                  <p>Choose the product category to begin.</p>
                </div>

                <div className="add-opportunity-panel-body">
                  <div className="form-group add-opportunity-coverage-select">
                    <label>Coverage Type</label>
                    <select
                      value={selectedCoverageType}
                      onChange={(e) => {
                        setSelectedCoverageType(e.target.value);
                        setSelectedPlanTypeId("");
                        setSelectedSubtype("");
                        setStep(1);
                        setPlanTypes([]);
                        setCarriers([]);
                        setFormData((prev) => ({
                          ...prev,
                          carrierId: "",
                          planName: "",
                          planId: "",
                          benefitOption: "",
                          benefitAmount: "",
                          termLength: "",
                          enrollmentPeriod: "",
                          enrollmentCode: "",
                          policyNumber: "",
                          faceValue: "",
                        }));
                      }}
                    >
                      <option value="">-- Select Coverage Type --</option>
                      {coverageTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="add-opportunity-panel">
                <div className="add-opportunity-panel-head">
                  <h4>{currentStepTitle}</h4>
                  <p>Select the subtype before entering detailed quote information.</p>
                </div>

                <div className="add-opportunity-panel-body">
                  <div className="form-group add-opportunity-coverage-select">
                    <label>
                      {selectedTypeName === "Life"
                        ? "Life Insurance Type"
                        : selectedTypeName === "Individual Health"
                        ? "Individual Health Type"
                        : selectedTypeName === "Medicare Supplement"
                        ? "Medicare Supplement Plan Type"
                        : "Plan Type"}
                    </label>
                    <select
                      value={selectedPlanTypeId}
                      onChange={(e) => {
                        const planTypeId = e.target.value;
                        const planType = planTypes.find(
                          (pt) => String(pt.id) === String(planTypeId)
                        );

                        setSelectedPlanTypeId(planTypeId);
                        setSelectedSubtype(planType?.name || "");
                      }}
                    >
                      <option value="">-- Select --</option>
                      {planTypes.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <>
                <div className="add-opportunity-summary-row">
                  <div className="add-opportunity-summary-card">
                    <div className="add-opportunity-summary-label">Coverage</div>
                    <div className="add-opportunity-summary-value">
                      {selectedTypeName || "Not Set"}
                    </div>
                  </div>

                  {selectedSubtype && (
                    <div className="add-opportunity-summary-card">
                      <div className="add-opportunity-summary-label">Plan Type</div>
                      <div className="add-opportunity-summary-value">{selectedSubtype}</div>
                    </div>
                  )}

                  <div className="add-opportunity-summary-card">
                    <div className="add-opportunity-summary-label">Carrier</div>
                    <div className="add-opportunity-summary-value">
                      {selectedCarrier?.name || "Not Set"}
                    </div>
                  </div>

                  {shouldShowPlanName && (
                    <div className="add-opportunity-summary-card">
                      <div className="add-opportunity-summary-label">Plan</div>
                      <div className="add-opportunity-summary-value">
                        {formData.planName || "Not Set"}
                      </div>
                    </div>
                  )}
                </div>

                <div className="add-opportunity-panel">
                  <div className="add-opportunity-panel-head">
                    <h4>Opportunity Details</h4>
                    <p>Configure quote details, plan details, timing, and follow-up actions.</p>
                  </div>

                  <div className="add-opportunity-panel-body">
                    <div className="add-opportunity-form-grid">
                      <div className="form-group">
                        <label>Status</label>
                        <select name="status" value={formData.status} onChange={handleChange}>
                          <option value="">-- Select Status --</option>
                          {opportunityStatuses.map((s) => (
                            <option key={s.id} value={s.name}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Next Step</label>
                        <select name="nextStep" value={formData.nextStep} onChange={handleChange}>
                          <option value="">-- Select Next Step --</option>
                          <option value="Follow Up">Follow Up</option>
                          <option value="Quote">Quote</option>
                          <option value="Submit Application">Submit Application</option>
                          <option value="Waiting on Documents">Waiting on Documents</option>
                          <option value="Review">Review</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Application Date</label>
                        <input
                          type="date"
                          name="applicationDate"
                          value={formData.applicationDate}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Effective Date</label>
                        <input
                          type="date"
                          name="effectiveDate"
                          value={formData.effectiveDate}
                          onChange={handleChange}
                        />
                      </div>

                      {renderCoverageSpecificFields()}

                      <div className="form-group">
                        <label>Premium</label>
                        <input
                          type="number"
                          name="premium"
                          value={formData.premium}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Due Date</label>
                        <input
                          type="date"
                          name="dueDate"
                          value={formData.dueDate}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Policy Number</label>
                        <input
                          type="text"
                          name="policyNumber"
                          value={formData.policyNumber}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Enrollment Code</label>
                        <input
                          type="text"
                          name="enrollmentCode"
                          value={formData.enrollmentCode}
                          onChange={handleChange}
                        />
                      </div>

                      <CustomFieldsSection
                        recordType="opportunity"
                        onValuesChange={setCustomFieldValues}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer add-opportunity-footer">
            <div className="modal-footer-left" />
            <div className="modal-footer-right">
              {step === 1 && (
                <>
                  <button className="modal-button" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    className="modal-button save"
                    onClick={handleNext}
                    disabled={!selectedCoverageType || coverageTypeLoading}
                  >
                    {coverageTypeLoading ? "Loading..." : "Continue"}
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <button className="modal-button" onClick={() => setStep(1)}>
                    Back
                  </button>
                  <button
                    className="modal-button save"
                    onClick={handleNextFromSubtype}
                    disabled={!selectedPlanTypeId}
                  >
                    Continue
                  </button>
                </>
              )}

              {step === 3 && (
                <>
                  <button
                    className="modal-button"
                    onClick={() => setStep(needsSubtypeStep ? 2 : 1)}
                  >
                    Back
                  </button>
                  <button className="modal-button save" onClick={handleSubmit}>
                    Add {selectedTypeName || "Opportunity"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOpportunityModal;